import { Button } from "@/components/ui/button"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Checkbox } from "@/components/ui/checkbox"
import { toast } from "@/components/ui/use-toast"
import {
  customer_owner_transfer_label,
  filter_customer_owner_options,
  toggle_customer_id,
} from "@/settings/customer_owner_helpers"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { get_display_name, safeExec, useAsyncEffect } from "@/utils"
import { zodResolver } from "@hookform/resolvers/zod"
import { useState } from "react"
import { useForm } from "react-hook-form"
import * as z from "zod"
import { api_fetch } from "../api/client.tsx"
import { CustomerAccount, User } from "../api/user.tsx"
import { PencilIcon } from "@heroicons/react/24/outline"
import { Trash2 } from "lucide-react"
import { UserState } from "@/auth/user.tsx"

const RESPONSIBILITIES = [
  // Operations
  { value: "BOLs / PODs", category: "Operations" },
  { value: "Purchase Orders", category: "Operations" },
  { value: "Freight / shipping", category: "Operations" },
  { value: "PO #s / DC names", category: "Operations" },

  // Finances
  { value: "Invoices", category: "Finances" },
  { value: "Proof that invoices were sent", category: "Finances" },

  // Sales
  { value: "Trade promotion validation", category: "Sales" },
  { value: "Distributor contract agreements", category: "Sales" },
]

const TEAMS = ["Operations", "Finance", "Sales", "Customer Success", "Other"]

const ROLES = [
  { value: "user", label: "User" },
  { value: "org_admin", label: "Org Admin" },
  { value: "broker", label: "Broker" },
]

const userSchema = z.object({
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  team: z.string().optional(),
  role: z.string().optional(),
  customer_ids: z.array(z.string()).optional(),
  access_customer_ids: z.array(z.string()).optional(),
  responsibilities: z
    .array(
      z.object({
        responsibility: z.string(),
        is_primary: z.boolean(),
      })
    )
    .optional(),
})

type UserFormData = z.infer<typeof userSchema>

interface UserEditFormProps {
  user: User
  users: User[]
  customers: CustomerAccount[]
  /** Whether the editor may change roles and account access (org admin / system admin). */
  canManageUsers: boolean
  isCurrentUser: boolean
  onSave: (data: UserFormData) => void
  onCancel: () => void
}

function UserEditForm({ user, users, customers, canManageUsers, isCurrentUser, onSave, onCancel }: UserEditFormProps) {
  const [customerSearch, setCustomerSearch] = useState("")
  const [accessSearch, setAccessSearch] = useState("")
  const form = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      first_name: user.first_name || "",
      last_name: user.last_name || "",
      team: user.team || "",
      role: user.role || "user",
      access_customer_ids: user.access_customers?.map((customer: CustomerAccount) => customer.id) || [],
      customer_ids: user.owned_customers?.map((customer: CustomerAccount) => customer.id) || [],
      responsibilities:
        user.responsibilities?.map((resp: string) => ({
          responsibility: resp,
          is_primary: user.is_primary_for?.includes(resp) || false,
        })) || [],
    },
  })

  const watchedResponsibilities = form.watch("responsibilities")
  const watchedCustomerIds = form.watch("customer_ids") || []
  const watchedRole = form.watch("role")
  const watchedAccessIds = form.watch("access_customer_ids") || []
  const filteredCustomers = filter_customer_owner_options(customers, customerSearch).slice(0, 100)
  const filteredAccessCustomers = filter_customer_owner_options(customers, accessSearch).slice(0, 100)

  function handleSubmit(data: UserFormData) {
    const payload = { ...data }
    // Role and account access are admin-only; the server rejects them from anyone else.
    if (!canManageUsers) {
      delete payload.role
      delete payload.access_customer_ids
    }
    // Only send a role when it actually changed (you can't change your own role).
    if (payload.role === user.role || isCurrentUser) {
      delete payload.role
    }
    onSave(payload)
  }

  function toggleResponsibility(responsibility: string) {
    const current = watchedResponsibilities || []
    const existing = current.find(r => r.responsibility === responsibility)

    if (existing) {
      // Remove responsibility
      form.setValue(
        "responsibilities",
        current.filter(r => r.responsibility !== responsibility)
      )
    } else {
      // Add responsibility
      form.setValue("responsibilities", [...current, { responsibility, is_primary: false }])
    }
  }

  function togglePrimary(responsibility: string) {
    const current = watchedResponsibilities || []
    const updated = current.map(r => (r.responsibility === responsibility ? { ...r, is_primary: !r.is_primary } : r))
    form.setValue("responsibilities", updated)
  }

  function toggleCustomer(customer_id: string) {
    form.setValue("customer_ids", toggle_customer_id(watchedCustomerIds, customer_id))
  }

  function toggleAccessCustomer(customer_id: string) {
    form.setValue("access_customer_ids", toggle_customer_id(watchedAccessIds, customer_id))
  }

  const groupedResponsibilities = RESPONSIBILITIES.reduce(
    (acc, resp) => {
      if (!acc[resp.category]) {
        acc[resp.category] = []
      }
      acc[resp.category].push(resp)
      return acc
    },
    {} as Record<string, typeof RESPONSIBILITIES>
  )

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} class="space-y-4">
        <div class="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="first_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="last_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="team"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Team</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select team" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {TEAMS.map(team => (
                    <SelectItem key={team} value={team}>
                      {team}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        {canManageUsers && (
          <FormField
            control={form.control}
            name="role"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Role</FormLabel>
                <Select onValueChange={field.onChange} value={field.value} disabled={isCurrentUser}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {ROLES.map(role => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p class="text-xs text-gray-500">
                  Brokers only see the promo calendar for the accounts granted below, can submit plans, but can't approve them.
                </p>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {canManageUsers && (
          <div>
            <FormLabel>Account access{watchedRole === "broker" ? "" : " (applies when role is Broker)"}</FormLabel>
            <div class="mt-2 rounded-md border border-gray-200">
              <div class="border-b border-gray-100 p-2">
                <Input
                  value={accessSearch}
                  onInput={(event: Event) => setAccessSearch((event.currentTarget as HTMLInputElement).value)}
                  placeholder="Search accounts"
                />
              </div>
              <div class="max-h-72 overflow-y-auto p-2">
                {filteredAccessCustomers.length ? (
                  <div class="space-y-1">
                    {filteredAccessCustomers.map((customer: CustomerAccount) => {
                      const checked = watchedAccessIds.includes(customer.id)
                      return (
                        <Label
                          key={customer.id}
                          className="flex cursor-pointer items-start gap-2 rounded px-2 py-1.5 hover:bg-gray-50">
                          <Checkbox checked={checked} onCheckedChange={() => toggleAccessCustomer(customer.id)} />
                          <span class="min-w-0 flex-1">
                            <span class="block truncate text-sm font-medium text-gray-900">{customer.name}</span>
                            <span class="block truncate text-xs text-gray-500">
                              {[customer.promomash_customer, customer.sales_rep].filter(Boolean).join(" - ") || customer.remote_id}
                            </span>
                          </span>
                        </Label>
                      )
                    })}
                  </div>
                ) : (
                  <div class="px-2 py-6 text-center text-sm text-gray-500">No accounts found</div>
                )}
              </div>
            </div>
            <div class="mt-1 text-xs text-gray-500">{watchedAccessIds.length} account(s) granted</div>
          </div>
        )}

        <div>
          <FormLabel>Responsibilities</FormLabel>
          <div class="space-y-4 mt-2">
            {Object.entries(groupedResponsibilities).map(([category, responsibilities]) => (
              <div key={category}>
                <h4 class="text-sm font-medium text-gray-900 mb-2">{category}</h4>
                <div class="space-y-2">
                  {responsibilities.map(resp => {
                    const isSelected = watchedResponsibilities?.some(r => r.responsibility === resp.value) || false
                    const isPrimary =
                      watchedResponsibilities?.find(r => r.responsibility === resp.value)?.is_primary || false

                    return (
                      <div key={resp.value} class="flex items-center space-x-2">
                        <Checkbox checked={isSelected} onCheckedChange={() => toggleResponsibility(resp.value)} />
                        <span class="text-sm">{resp.value}</span>
                        {isSelected && (
                          <div class="flex items-center space-x-1">
                            <Checkbox checked={isPrimary} onCheckedChange={() => togglePrimary(resp.value)} />
                            <span class="text-xs text-gray-500">Primary</span>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <FormLabel>Customer ownership</FormLabel>
          <div class="mt-2 rounded-md border border-gray-200">
            <div class="border-b border-gray-100 p-2">
              <Input
                value={customerSearch}
                onInput={(event: Event) => setCustomerSearch((event.currentTarget as HTMLInputElement).value)}
                placeholder="Search customers"
              />
            </div>
            <div class="max-h-72 overflow-y-auto p-2">
              {filteredCustomers.length ? (
                <div class="space-y-1">
                  {filteredCustomers.map((customer: CustomerAccount) => {
                    const checked = watchedCustomerIds.includes(customer.id)
                    const transferLabel = customer_owner_transfer_label(customer.id, users, user.id)
                    return (
                      <Label
                        key={customer.id}
                        className="flex cursor-pointer items-start gap-2 rounded px-2 py-1.5 hover:bg-gray-50">
                        <Checkbox checked={checked} onCheckedChange={() => toggleCustomer(customer.id)} />
                        <span class="min-w-0 flex-1">
                          <span class="block truncate text-sm font-medium text-gray-900">{customer.name}</span>
                          <span class="block truncate text-xs text-gray-500">
                            {[customer.promomash_customer, customer.sales_rep].filter(Boolean).join(" - ") || customer.remote_id}
                          </span>
                          {transferLabel ? <span class="block text-xs text-amber-700">{transferLabel}</span> : null}
                        </span>
                      </Label>
                    )
                  })}
                </div>
              ) : (
                <div class="px-2 py-6 text-center text-sm text-gray-500">No customers found</div>
              )}
            </div>
          </div>
          <div class="mt-1 text-xs text-gray-500">{watchedCustomerIds.length} customer(s) selected</div>
        </div>

        <div class="flex justify-end space-x-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit">Save Changes</Button>
        </div>
      </form>
    </Form>
  )
}

interface UserRowProps {
  user: User
  onEdit: (user: User) => void
  onDelete: (user: User) => void
  canDeleteUsers: boolean
  isCurrentUser: boolean
}

function UserRow({ user, onEdit, onDelete, canDeleteUsers, isCurrentUser }: UserRowProps) {
  const ownedCustomers = user.owned_customers || []
  const ownedCustomerSummary =
    ownedCustomers.length > 3
      ? `${ownedCustomers.slice(0, 3).map((customer: CustomerAccount) => customer.name).join(", ")} +${ownedCustomers.length - 3} more`
      : ownedCustomers.map((customer: CustomerAccount) => customer.name).join(", ")

  return (
    <div class="flex items-center justify-between py-3 border-b border-gray-100">
      <div class="flex items-center gap-4">
        <div>
          <div class="text-sm font-medium text-gray-900">
            {get_display_name(user)}
            {user.role === "broker" && (
              <span class="ml-2 rounded bg-purple-100 px-1.5 py-0.5 text-xs font-medium text-purple-800">Broker</span>
            )}
            {user.role === "org_admin" && (
              <span class="ml-2 rounded bg-blue-100 px-1.5 py-0.5 text-xs font-medium text-blue-800">Admin</span>
            )}
          </div>
          <div class="text-xs text-gray-500">{user.email}</div>
        </div>
        <div class="text-xs text-gray-500">{user.team && <span class="mr-2">{user.team}</span>}</div>
        <div class="text-xs text-gray-500">
          {user.responsibilities?.length ? (
            <span>{user.responsibilities.join(", ")}</span>
          ) : (
            <span class="text-gray-400">No responsibilities</span>
          )}
        </div>
        <div class="max-w-xs text-xs text-gray-500">
          {ownedCustomers.length ? (
            <span>{ownedCustomerSummary}</span>
          ) : (
            <span class="text-gray-400">No customers</span>
          )}
        </div>
        {user.is_primary_for?.length ? (
          <div class="text-xs">
            {user.is_primary_for.map((resp: string) => (
              <span key={resp} class="bg-yellow-100 text-yellow-800 px-1 py-0.5 rounded mr-1">
                ⭐ {resp}
              </span>
            ))}
          </div>
        ) : null}
      </div>
      <div class="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => onEdit(user)} class="flex items-center gap-2">
          <PencilIcon class="h-4 w-4" />
          Edit
        </Button>
        {canDeleteUsers ? (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isCurrentUser}
                class="text-red-600 hover:bg-red-50 hover:text-red-700"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete user?</AlertDialogTitle>
                <AlertDialogDescription>
                  {isCurrentUser
                    ? "You cannot delete your own user account."
                    : `This will remove ${user.email} from the users page and sign them out of active sessions.`}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-red-600 text-white hover:bg-red-700"
                  onClick={() => onDelete(user)}
                  disabled={isCurrentUser}
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        ) : null}
      </div>
    </div>
  )
}

export function UserManagement() {
  const [users, setUsers] = useState<User[]>([])
  const [customers, setCustomers] = useState<CustomerAccount[]>([])
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const currentUser = UserState.use()
  const canDeleteUsers = currentUser?.role === "system_admin" || currentUser?.role === "org_admin"

  async function loadUsers() {
    const res = await api_fetch<User[]>("/users")
    if (res.ok) {
      setUsers(res.value.data)
    }
  }

  async function loadCustomers() {
    const res = await api_fetch<CustomerAccount[]>("/customers", { use_cache: true })
    if (res.ok) {
      setCustomers(res.value.data)
    }
  }

  useAsyncEffect(async () => {
    await Promise.all([loadUsers(), loadCustomers()])
  }, [])

  async function handleSaveUser(data: UserFormData) {
    if (!editingUser) return
    console.log(editingUser, data)

    safeExec(async () => {
      const res = await api_fetch<User>(`/users/${editingUser.id}`, {
        method: "PATCH",
        body: data,
      })

      if (res.ok) {
        toast({
          title: "User Updated",
          description: `User ${editingUser.email} has been updated successfully`,
        })
        setEditingUser(null)
        await loadUsers()
      } else {
        toast({
          title: "Failed to Update User",
          description: res.error?.message || "An error occurred",
          variant: "destructive",
        })
      }
    })
  }

  async function handleDeleteUser(user: User) {
    safeExec(async () => {
      const res = await api_fetch<void>(`/users/${user.id}`, {
        method: "DELETE",
      })

      if (res.ok) {
        toast({
          title: "User Deleted",
          description: `${user.email} has been removed from the users page`,
        })
        await loadUsers()
      } else {
        toast({
          title: "Failed to Delete User",
          description: res.error?.message || "An error occurred",
          variant: "destructive",
        })
      }
    })
  }

  return (
    <div class="space-y-6">
      <div>
        <h3 class="text-sm font-medium text-gray-900 mb-3">Team Members</h3>

        {editingUser ? (
          <div class="bg-white border rounded-lg p-4">
            <h4 class="text-sm font-medium text-gray-900 mb-4">Edit {get_display_name(editingUser)}</h4>
            <UserEditForm
              user={editingUser}
              users={users}
              customers={customers}
              canManageUsers={canDeleteUsers}
              isCurrentUser={currentUser?.id === editingUser.id}
              onSave={handleSaveUser}
              onCancel={() => setEditingUser(null)}
            />
          </div>
        ) : (
          <div class="space-y-2">
            {users.map((user: User) => (
              <UserRow
                key={user.id}
                user={user}
                onEdit={setEditingUser}
                onDelete={handleDeleteUser}
                canDeleteUsers={canDeleteUsers}
                isCurrentUser={currentUser?.id === user.id}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
